import type { Cart } from "@commercetools/platform-sdk";
import { useCallback } from "react";
import { isCommercetoolsHttpResponseError } from "../utils/commercetools/fetch-from-ct";
import {
	type ExpandedOrder,
	getOrderByCartId as fetchOrderByCartId,
	getOrderById,
} from "../utils/commercetools/order-service";
import { useCartService } from "./use-cart-service";

interface OrderServiceMethods {
	createOrder: (cart: Cart) => Promise<ExpandedOrder>;
	getOrder: (orderId: string) => Promise<ExpandedOrder>;
	getOrderByCart: (cart: Cart) => Promise<ExpandedOrder>;
	getOrderByCartId: (cartId: string) => Promise<ExpandedOrder>;
}

export const useOrderService = (): OrderServiceMethods => {
	const { createOrderFromCart } = useCartService();

	const getOrderByCart: OrderServiceMethods["getOrderByCart"] = useCallback(
		async (cart) => await fetchOrderByCartId(cart.id),
		[],
	);

	const createOrder: OrderServiceMethods["createOrder"] = useCallback(
		async (cart) => {
			try {
				return await createOrderFromCart();
			} catch (error) {
				if (
					isCommercetoolsHttpResponseError(error) &&
					error.statusCode === 409
				) {
					// In this case there is a risk that the order creation has already been done via a webhook so we see if we got an order already.
					// We wait 2 seconds just to give the webhook some time to finish in case
					await new Promise((resolve) => setTimeout(resolve, 2000));
					return await getOrderByCart(cart);
				} else {
					throw error;
				}
			}
		},
		[createOrderFromCart, getOrderByCart],
	);

	const getOrder: OrderServiceMethods["getOrder"] = useCallback(
		async (orderId) => await getOrderById(orderId),
		[],
	);

	const getOrderByCartId: OrderServiceMethods["getOrderByCartId"] = useCallback(
		async (cartId) => await fetchOrderByCartId(cartId),
		[],
	);
	return {
		createOrder,
		getOrderByCartId,
		getOrder,
		getOrderByCart,
	};
};
